/**
 * Heroicons drawn onto the grid canvas.
 *
 * The grid paints through a 2D context, so it cannot render the React icon
 * components the rest of the app uses. To keep one icon vocabulary across the
 * product, the path data below is copied verbatim from the same Heroicons v2
 * package (`@heroicons/react/24/solid`) that the surrounding UI imports — so the
 * funnel on a column header is literally the same shape as the funnel in the
 * menu that opens from it.
 *
 * Solid rather than outline on purpose: an outline icon is a 1.5px stroke on a
 * 24px canvas, and scaling that to 11px leaves a 0.7px hairline that renders
 * washed out. A filled path keeps its weight at any size.
 */

const HEROICON_VIEWBOX = 24;

export const CANVAS_ICON = {
  /** Heroicons v2 solid · funnel — a filter is applied. */
  funnel:
    "M3.792 2.938A49.069 49.069 0 0 1 12 2.25c2.797 0 5.54.236 8.209.688a1.857 1.857 0 0 1 1.541 1.836v1.044a3 3 0 0 1-.879 2.121l-6.182 6.182a1.5 1.5 0 0 0-.439 1.061v2.927a3 3 0 0 1-1.658 2.684l-1.757.878A.75.75 0 0 1 9.75 21v-5.818a1.5 1.5 0 0 0-.44-1.06L3.13 7.938a3 3 0 0 1-.879-2.121V4.774c0-.897.64-1.683 1.542-1.836Z",
  /** Heroicons v2 solid · bars-arrow-down — sorted descending. */
  barsArrowDown:
    "M2.25 4.5A.75.75 0 0 1 3 3.75h14.25a.75.75 0 0 1 0 1.5H3a.75.75 0 0 1-.75-.75Zm0 4.5A.75.75 0 0 1 3 8.25h9.75a.75.75 0 0 1 0 1.5H3A.75.75 0 0 1 2.25 9Zm15-.75A.75.75 0 0 1 18 9v10.19l2.47-2.47a.75.75 0 1 1 1.06 1.06l-3.75 3.75a.75.75 0 0 1-1.06 0l-3.75-3.75a.75.75 0 1 1 1.06-1.06l2.47 2.47V9a.75.75 0 0 1 .75-.75Zm-15 5.25a.75.75 0 0 1 .75-.75h9.75a.75.75 0 0 1 0 1.5H3a.75.75 0 0 1-.75-.75Z",
  /** Heroicons v2 solid · bars-arrow-up — sorted ascending. */
  barsArrowUp:
    "M2.25 4.5A.75.75 0 0 1 3 3.75h14.25a.75.75 0 0 1 0 1.5H3a.75.75 0 0 1-.75-.75Zm14.47 3.97a.75.75 0 0 1 1.06 0l3.75 3.75a.75.75 0 1 1-1.06 1.06L18 10.81V21a.75.75 0 0 1-1.5 0V10.81l-2.47 2.47a.75.75 0 1 1-1.06-1.06l3.75-3.75ZM2.25 9A.75.75 0 0 1 3 8.25h9.75a.75.75 0 0 1 0 1.5H3A.75.75 0 0 1 2.25 9Zm0 4.5a.75.75 0 0 1 .75-.75h5.25a.75.75 0 0 1 0 1.5H3a.75.75 0 0 1-.75-.75Z",
} as const;

export type CanvasIconName = keyof typeof CANVAS_ICON;

// Path2D construction is not free and these are redrawn on every grid paint, so
// each icon is parsed once for the life of the tab.
const pathCache = new Map<string, Path2D>();

const iconPath = (d: string): Path2D => {
  const cached = pathCache.get(d);
  if (cached) return cached;

  const path = new Path2D(d);
  pathCache.set(d, path);
  return path;
};

export interface CanvasIconOptions {
  /** Center of the icon, in canvas pixels. */
  centerX: number;
  centerY: number;
  /** Rendered edge length; the 24px viewBox is scaled to fit it. */
  size: number;
  color: string;
  alpha?: number;
}

/**
 * Three lines stepping shorter — the conventional "this column can be filtered
 * or sorted" glyph.
 *
 * Drawn rather than taken from Heroicons because the library has no progressively
 * narrowing bars: its closest shape (`bars-3-bottom-left`) is long/long/half, which
 * at this size reads as an equals sign with a tail rather than a filter. Drawing it
 * also lets the weight be set in device pixels, so it stays emphatic at 13px where
 * a scaled-down 1.5-unit stroke goes hazy.
 */
export const drawFilterLinesMark = (
  ctx: CanvasRenderingContext2D,
  {
    centerX,
    centerY,
    width,
    color,
    alpha,
  }: {
    centerX: number;
    centerY: number;
    width: number;
    color: string;
    alpha?: number;
  },
): void => {
  const thickness = 2;
  const gap = thickness + 1.8;
  const widths = [width, width * 0.68, width * 0.36];
  const left = centerX - width / 2;
  const top = centerY - gap;

  ctx.save();
  if (alpha != null) ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = thickness;
  ctx.lineCap = "round";

  widths.forEach((lineWidth, index) => {
    // An even-width stroke stays crisp when its center is on an integer pixel.
    const y = Math.round(top + index * gap);
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(left + lineWidth, y);
    ctx.stroke();
  });

  ctx.restore();
};

export const drawCanvasIcon = (
  ctx: CanvasRenderingContext2D,
  icon: CanvasIconName,
  { centerX, centerY, size, color, alpha }: CanvasIconOptions,
): void => {
  const scale = size / HEROICON_VIEWBOX;

  ctx.save();
  if (alpha != null) ctx.globalAlpha = alpha;
  ctx.translate(centerX - size / 2, centerY - size / 2);
  ctx.scale(scale, scale);
  ctx.fillStyle = color;
  // Heroicons' funnel relies on even-odd winding; the others are unaffected by it.
  ctx.fill(iconPath(CANVAS_ICON[icon]), "evenodd");
  ctx.restore();
};
